// A channel (M2-06, CHAT-UX; spec 06 §Channels and topics), Slack's shape with topics: a thin
// header (the channel, or "#channel › topic" when one is open, with the topic's visible
// controls — pin, close, archive, members — and its "…"; description, who is here, the member
// stack with the DEs' faces and "Add people" (CHAT-DE), "New topic", the bell and the "…" menu),
// a light strip of the channel's topics, the agent room shell when it is one, then the messages
// and the composer. CHAT-TOPICS-SEPARATE: the channel and its topics are separate places. The
// channel shows the messages posted to it (its own conversation, the `(no topic)` topic) merged
// in time order with its topics' latest messages, each tagged with its topic (standing rule);
// a topic (from the strip, the sidebar's disclosure or a deep link) shows only its own, to its
// members — an open topic's non-member sees a join prompt. The composer names where it posts.
// DMs stay one conversation. Opening a channel remembers it as the company's last one.
import {
  AvatarTile,
  Badge,
  Button,
  type ComposerValue,
  CountPill,
  EmptyState,
} from "@companyos/ui";
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronDownIcon,
  CircleCheckIcon,
  HashIcon,
  LockIcon,
  PinIcon,
  PlusIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { keepFocus, useFocus } from "../../focus/focus";
import { useShellState } from "../../shell/shell-state";
import {
  useOwnBreakGlass,
  ViewedUnderBreakGlassBadge,
  viewedUnderBreakGlass,
} from "../agency/company-break-glass";
import { LoadError } from "../screen-parts";
import { useSession } from "../use-feature";
import { ago } from "../work/doc-kinds";
import { FavoriteStar } from "../work/work-screens";
import { conversationVersion, useAddressee } from "./addressee";
import { AgentRoomHeader, type RoomAgent } from "./agent-room";
import { AgentWorkingLine } from "./agent-wakes";
import { AgentStatusLine } from "./cards";
import { AddPeopleButton, MemberStack } from "./channel-agents";
import { ChannelDetailsDialog, type DetailsTab } from "./channel-details";
import { ChannelFilesButton } from "./channel-files-tab";
import { ChannelMenu, NotificationBell } from "./channel-settings";
import type { ChatFile } from "./chat-files";
import {
  channelLabel,
  dmMemberIds,
  interleave,
  isAgentRoom,
  isDm,
  lastReadId,
  MAIN_TOPIC,
  mainLineAsks,
  mainLineRuns,
  mainTopic,
  orderedTopics,
  realTopics,
  replyingByRoot,
  runsInTopic,
  threadsOf,
  withRoots,
  workingAgents,
  writeLastChannel,
} from "./chat-model";
import { markReadOptimistic } from "./chat-optimistic";
import {
  type Channel,
  channelQuery,
  chatKeys,
  latestMessagesQuery,
  locateQuery,
  type MessageView,
  messagesQuery,
  type Run,
  type TopicSummary,
  topicQuery,
  topicsQuery,
} from "./chat-queries";
import { useReconcileUnsent } from "./chat-switch";
import {
  Conversation,
  flatMessages,
  toChatMessages,
  useAgentRoom,
  useMessageActions,
  usePost,
  useRuns,
  useSeatLabels,
} from "./conversation";
import { MemberPresenceInfo, useStatuses } from "./member-status";
import { STREAM_TOPICS } from "./prefetch";
import { PresenceDot, presenceState } from "./presence-dot";
import { useDeliveries } from "./request-card";
import { useSuggestions } from "./suggestion-cards";
import { useThreadReads } from "./thread-reads";
import { NewTopicDialog, TopicMenu, TopicQuickActions } from "./topic-actions";
import { TopicJoinPrompt, TopicMembersButton } from "./topic-members";
import { type ChatContext, useChat, usePresence } from "./use-chat";

/** At most this many unloaded thread roots are fetched for one view. */
const MISSING_ROOTS_MAX = 10;

/**
 * CHAT-PLACEMENT-FIX: the loaded messages split into the main line and reply threads, with the
 * roots of replies whose thread started before the loaded pages fetched (`messages.locate`), so
 * a long thread stays one line with its replies under it instead of spilling onto the main line.
 */
function useThreadSplit<T extends MessageView>(chat: ChatContext, list: readonly T[]) {
  const { api, co } = chat;
  const first = useMemo(() => threadsOf(list), [list]);
  const located = useQueries({
    queries: first.missing.slice(0, MISSING_ROOTS_MAX).map((id) => locateQuery(api, co, id)),
  });
  const roots = located.map((q) => q.data?.root as T | undefined);
  const key = roots.map((r) => r?.id ?? "").join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the located roots.
  const all = useMemo(() => withRoots(list, roots), [list, key]);
  const split = useMemo(
    () => (all.length === list.length ? first : threadsOf(all)),
    [all, list, first],
  );
  return { all, split };
}

/** A clock that ticks once a minute (edit windows, relative times). */
function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const LIVE_STATES = new Set<Run["state"]>([
  "queued",
  "running",
  "waiting_input",
  "awaiting_approval",
  "waiting_children",
]);

export function ChannelScreen({
  channelId,
  topicId,
  embedded = false,
}: {
  channelId: string;
  topicId?: string | undefined;
  /**
   * LIVE-WORKSPACE: shown docked beside another page (Live's chat dock), not as Chat's own page:
   * it is not remembered as the company's last channel.
   */
  embedded?: boolean;
}) {
  const chat = useChat();
  const { api, co, company } = chat;
  const navigate = useNavigate();
  const channel = useQuery(channelQuery(api, co, channelId));
  // Archived topics come along for the channel's admins (the server keeps them from everyone
  // else); the lists leave them out, a deep link still opens one.
  const topics = useQuery(topicsQuery(api, co, channelId));
  const here = usePresence(
    api,
    co,
    topicId ? { kind: "topic", id: topicId } : { kind: "channel", id: channelId },
  );
  const runs = useRuns(chat);
  const now = useMinuteClock();
  // Where `/chat` lands next time in this company.
  useEffect(() => {
    if (!embedded) writeLastChannel(co, channelId);
  }, [co, channelId, embedded]);
  // CHAT-SWITCH-B: sends a reload cut off go again under their own idempotency keys (once).
  useReconcileUnsent(chat);

  const ch = channel.data;
  const all = topics.data?.data;
  const dm = ch ? isDm(ch) : false;
  // CHAT-TOPICS-SEPARATE: the channel's own conversation is never listed as a topic (a DM's
  // topics are its one conversation).
  const main = dm ? undefined : mainTopic(all ?? []);
  const list = useMemo(() => orderedTopics(dm ? (all ?? []) : realTopics(all ?? [])), [all, dm]);
  // A channel opens its own conversation until a topic is picked.
  const listed = (all ?? []).find((t) => t.id === topicId);
  // A deep link to a topic the list leaves out (archived, for a member) still opens it.
  const direct = useQuery({
    ...topicQuery(api, co, topicId ?? ""),
    enabled: topicId !== undefined && topics.isSuccess && !listed,
  });
  const open =
    listed ??
    (direct.data && direct.data.channel_id === channelId
      ? { ...direct.data, unread_count: 0, following: false, member_count: 0 }
      : undefined);
  // A link naming the channel's own conversation as a topic opens the channel (the thread kept).
  const toChannel = open?.is_main === true && !dm;
  useEffect(() => {
    if (!toChannel) return;
    void navigate({
      to: "/c/$slug/chat/$channelId",
      params: { slug: company.slug, channelId },
      search: ((prev: Record<string, unknown>) => prev) as never,
      replace: true,
    });
  }, [toChannel, navigate, company.slug, channelId]);

  if (channel.isError)
    return (
      <div className="p-6">
        <LoadError what="This channel" error={channel.error} />
      </div>
    );
  if (!ch)
    return (
      <div role="status" aria-label="Loading channel" className="p-8 text-sm text-muted-foreground">
        Loading…
      </div>
    );

  const room = isAgentRoom(ch);
  const shown = open && !toChannel ? open : undefined;
  // A topic's messages show to its members only — except to the agency operator (no membership
  // here, Andrei 2026-10-28): it reads every open topic read-only, without joining (no composer:
  // it has no membership to post with).
  const joinFirst = shown !== undefined && !shown.is_member && !shown.is_main && chat.meId !== null;

  return (
    <div
      data-slot="chat-channel"
      data-channel-kind={ch.kind}
      className="flex h-full min-h-0 min-w-0 flex-col"
    >
      <ChannelHeader chat={chat} channel={ch} here={here} topic={shown} />
      {!dm ? <TopicStrip chat={chat} channel={ch} topics={list} open={topicId} now={now} /> : null}
      {room ? (
        <AgentRoomHeader
          chat={chat}
          channel={ch}
          agents={agentsOf(chat, runs, all ?? [])}
          noteThreadId={shown?.thread_id ?? main?.thread_id ?? list[0]?.thread_id ?? null}
        />
      ) : null}
      {topics.isError ? (
        <div className="p-6">
          <LoadError what="The topics" error={topics.error} />
        </div>
      ) : dm ? (
        // A DM is one conversation: every topic's messages in one stream, no topic line.
        <DmPane chat={chat} channel={ch} topics={list} runs={runs} now={now} />
      ) : topicId !== undefined ? (
        toChannel ? null : shown ? (
          joinFirst ? (
            <TopicJoinPrompt key={shown.id} chat={chat} topic={shown} />
          ) : (
            <TopicPane
              key={shown.id}
              chat={chat}
              channel={ch}
              topic={shown}
              runs={runs}
              now={now}
            />
          )
        ) : topics.isSuccess && !direct.isPending ? (
          <EmptyState
            className="m-auto"
            icon={HashIcon}
            title="This topic is not here"
            description="It may have moved to another channel."
          />
        ) : null
      ) : !topics.isSuccess ? (
        <div
          role="status"
          aria-label="Loading messages"
          className="p-6 text-sm text-muted-foreground"
        >
          Loading messages…
        </div>
      ) : main ? (
        <TopicPane
          key={main.id}
          chat={chat}
          channel={ch}
          topic={main}
          runs={runs}
          now={now}
          main
          topics={list}
        />
      ) : (
        // The channel's own conversation is made with its first message.
        <EmptyChannelPane
          chat={chat}
          channel={ch}
          anyThread={list[0]?.thread_id ?? null}
          topics={list}
          runs={runs}
          now={now}
        />
      )}
    </div>
  );
}

/** The agents seen in a room: DEs with runs on its topics, each with its latest live run. */
function agentsOf(
  chat: ChatContext,
  runs: readonly Run[],
  topics: readonly TopicSummary[],
): RoomAgent[] {
  const ids = new Set(topics.map((t) => t.id));
  const out = new Map<string, RoomAgent>();
  for (const r of runs) {
    if (r.trigger_ref?.kind !== "topic" || !r.trigger_ref.id || !ids.has(r.trigger_ref.id))
      continue;
    const prior = out.get(r.membership_id);
    const live = LIVE_STATES.has(r.state);
    if (!prior || (!prior.run && live))
      out.set(r.membership_id, {
        id: r.membership_id,
        name: chat.members.get(r.membership_id)?.name ?? "Agent",
        ...(live ? { run: r } : {}),
      });
  }
  // Without the agents add-on (no runs to read) the room names the company's DEs.
  if (!out.size)
    for (const m of chat.members.values())
      if (m.kind === "de") out.set(m.id, { id: m.id, name: m.name });
  return [...out.values()];
}

/**
 * The thin header (CHAT-UX): the channel — or "#channel › topic" with the topic's visible
 * controls (pin, close, archive; its "…" for rename, move, copy link, delete) — the
 * description, who is here, the member count, "New topic", the bell and the "…" menu. No hero,
 * no paragraph.
 */
function ChannelHeader({
  chat,
  channel,
  here,
  topic,
}: {
  chat: ChatContext;
  channel: Channel;
  here: ReadonlySet<string>;
  /** The open topic, when one is. */
  topic: TopicSummary | undefined;
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  // CHAT-CONTROLS: the channel's name opens its details (Slack's header), on About; WF-FIX
  // (2026-10-26): the DEs' chip opens them on Agents.
  const [details, setDetails] = useState<DetailsTab | null>(null);
  const present = [...here].flatMap((id) => {
    const m = chat.members.get(id);
    return m && id !== chat.meId ? [m] : [];
  });
  const dm = isDm(channel);
  // CHAT-SWITCH-A: the people's active / away / do-not-disturb; a person DM's status, presence
  // and local time beside its name.
  const statuses = useStatuses(chat.api, chat.co, chat.meId !== null);
  const dmOther = dm ? dmMemberIds(channel).find((id) => id !== chat.meId) : undefined;
  const dmStatus =
    dmOther && chat.members.get(dmOther)?.kind !== "de" ? statuses.get(dmOther) : undefined;
  const clock = useMinuteClock();
  const closed = topic?.status === "resolved";
  const canWrite = chat.meId !== null && !chat.readOnly;
  const backToChannel = () =>
    void navigate({
      to: "/c/$slug/chat/$channelId",
      params: { slug: chat.company.slug, channelId: channel.id },
      search: keepFocus as never,
    });
  // BREAK-GLASS: an agency operator's live session is the only way it sees a private channel.
  const { me } = useSession();
  const own = useOwnBreakGlass(chat.api, chat.company, me.user.id);
  const underBreakGlass = viewedUnderBreakGlass(own, channel, chat.meId !== null);
  return (
    <header
      data-slot="channel-header"
      className="group/header relative flex h-11 min-w-0 shrink-0 items-center gap-2 strip-scroll border-b border-border-subtle pr-2 pl-5 max-md:pl-3"
    >
      {channel.is_private && !dm ? (
        <LockIcon aria-label="Private" className="size-4 shrink-0 text-faint" />
      ) : null}
      <h1 className="flex min-w-28 shrink items-center gap-1.5 truncate text-[15px] font-semibold text-foreground">
        {topic ? (
          <>
            <Link
              to="/c/$slug/chat/$channelId"
              params={{ slug: chat.company.slug, channelId: channel.id }}
              search={keepFocus as never}
              title={`Back to ${channelLabel(channel, chat)}`}
              className="max-w-[14ch] shrink-0 truncate text-secondary hover:text-foreground hover:underline"
            >
              {channelLabel(channel, chat)}
            </Link>
            <span aria-hidden="true" className="shrink-0 text-faint">
              ›
            </span>
            {topic.is_private ? (
              <LockIcon aria-label="Private topic" className="size-3.5 shrink-0 text-faint" />
            ) : null}
            {topic.pinned_at ? (
              <PinIcon aria-label="Pinned" className="size-3.5 shrink-0 text-faint" />
            ) : null}
            {closed ? (
              <CircleCheckIcon aria-label="Closed" className="size-4 shrink-0 text-success" />
            ) : null}
            <span className={`min-w-0 truncate ${closed ? "text-muted-foreground" : ""}`}>
              {topic.name}
            </span>
          </>
        ) : (
          <button
            type="button"
            data-slot="channel-name"
            aria-label={`Channel details: ${channelLabel(channel, chat)}`}
            title="Channel details"
            onClick={() => setDetails("about")}
            className="-ml-1.5 inline-flex h-7 shrink-0 items-center gap-0.5 rounded-md px-1.5 text-[15px] font-semibold text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
          >
            <span className="max-w-[32ch] truncate">{channelLabel(channel, chat)}</span>
            <ChevronDownIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
          </button>
        )}
      </h1>
      {dmStatus && !topic ? (
        <MemberPresenceInfo
          status={dmStatus}
          now={clock}
          inline
          className="min-w-0 flex-1 max-sm:hidden"
        />
      ) : null}
      {/* WOS-BUNDLE: star the channel (Work's Favorites list it). */}
      {!topic && !dm ? (
        <FavoriteStar
          company={chat.company}
          favorite={{ kind: "channel", id: channel.id }}
          name={channelLabel(channel, chat)}
        />
      ) : null}
      {channel.archived_at && !topic ? <Badge variant="outline">Archived</Badge> : null}
      {topic?.archived_at ? <Badge variant="outline">Archived</Badge> : null}
      {topic && !dm ? (
        <div data-slot="topic-header-actions" className="ml-1 flex shrink-0 items-center gap-px">
          <TopicQuickActions chat={chat} channel={channel} topic={topic} />
          {/* CHAT-TOPICS-SEPARATE: who is in the topic (add, remove, leave, private). */}
          <TopicMembersButton chat={chat} channel={channel} topic={topic} />
          <TopicMenu
            chat={chat}
            channel={channel}
            topic={topic}
            className="text-faint hover:text-foreground"
            onDeleted={backToChannel}
          />
        </div>
      ) : null}
      {isAgentRoom(channel) ? <Badge variant="accent">Agent room</Badge> : null}
      {channel.kind === "board_home" ? <Badge variant="outline">Board channel</Badge> : null}
      {underBreakGlass ? <ViewedUnderBreakGlassBadge /> : null}
      {channel.description && !topic ? (
        <>
          <span aria-hidden="true" className="h-4 w-px shrink-0 bg-border max-sm:hidden" />
          <p
            data-slot="channel-description"
            className="m-0 min-w-0 flex-1 truncate text-sm text-muted-foreground max-sm:hidden"
          >
            {channel.description}
          </p>
        </>
      ) : null}
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {present.length ? (
          <ul
            aria-label="Here now"
            className="m-0 mr-1.5 flex list-none items-center -space-x-1 p-0 max-sm:hidden"
          >
            {present.slice(0, 3).map((m) => (
              <li key={m.id} title={`${m.name} is here`} className="relative">
                <AvatarTile
                  name={m.name}
                  kind={m.kind === "de" ? "agent" : "person"}
                  size="xs"
                  className="ring-2 ring-card"
                />
                <PresenceDot
                  className="absolute -right-0.5 -bottom-0.5"
                  {...(m.kind === "de" || !statuses.get(m.id)
                    ? { label: `${m.name} is here` }
                    : { state: presenceState(statuses.get(m.id)) })}
                />
              </li>
            ))}
          </ul>
        ) : null}
        {/* CHAT-DE: the members' faces (DEs first) and the count, then one "Add people". */}
        {!dm ? (
          <MemberStack chat={chat} channel={channel} onOpen={() => setDetails("members")} />
        ) : null}
        {!dm ? <AddPeopleButton chat={chat} channel={channel} /> : null}
        {canWrite && !dm ? (
          <Button
            variant="ghost"
            size="xs"
            className="mr-0.5 text-secondary"
            onClick={() => setCreating(true)}
          >
            <PlusIcon data-icon="inline-start" />
            <span className="max-sm:sr-only">New topic</span>
          </Button>
        ) : null}
        {/* CHAT-SWITCH-A: the channel's Files (Slack's Files tab). */}
        <ChannelFilesButton chat={chat} channel={channel} />
        <NotificationBell chat={chat} channel={channel} />
        <ChannelMenu chat={chat} channel={channel} />
      </div>
      {creating ? (
        <NewTopicDialog chat={chat} channel={channel} onClose={() => setCreating(false)} />
      ) : null}
      {details ? (
        <ChannelDetailsDialog
          chat={chat}
          channel={channel}
          tab={details}
          onClose={() => setDetails(null)}
        />
      ) : null}
    </header>
  );
}

/**
 * The topic strip under the header (CHAT-TOPICS-SEPARATE, Slack-light): the channel itself, then
 * each topic — pinned first (with the pin), its unread count (a member's only) and its last
 * activity — the open one marked. Only when the channel has topics. No topic messages here.
 */
function TopicStrip({
  chat,
  channel,
  topics,
  open,
  now,
}: {
  chat: ChatContext;
  channel: Channel;
  topics: readonly TopicSummary[];
  /** The open topic's id, if one is. */
  open: string | undefined;
  now: Date;
}) {
  if (!topics.length) return null;
  const params = { slug: chat.company.slug, channelId: channel.id };
  const chip = (active: boolean) =>
    `inline-flex h-6 shrink-0 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors ${
      active
        ? "border-primary/40 bg-primary-soft text-primary-text"
        : "border-border text-secondary hover:border-border-strong hover:text-foreground"
    }`;
  return (
    <nav
      data-slot="topic-strip"
      aria-label="Topics"
      className="flex min-w-0 max-w-full shrink-0 items-center gap-1.5 strip-scroll border-b border-border-subtle px-5 py-1.5 max-md:px-3"
    >
      <Link
        to="/c/$slug/chat/$channelId"
        params={params}
        search={keepFocus as never}
        className={chip(open === undefined)}
        aria-current={open === undefined ? "page" : undefined}
        title={`${channelLabel(channel, chat)} itself`}
      >
        {channelLabel(channel, chat)}
      </Link>
      {topics.map((t) => {
        const unread = t.is_member ? t.unread_count : 0;
        const at = t.last_message_at ?? null;
        return (
          <Link
            key={t.id}
            data-topic={t.id}
            to="/c/$slug/chat/$channelId/$topicId"
            params={{ ...params, topicId: t.id }}
            search={keepFocus as never}
            className={chip(open === t.id)}
            aria-current={open === t.id ? "page" : undefined}
            title={t.pinned_at ? `Pinned: ${t.name}` : t.name}
          >
            {t.pinned_at ? <PinIcon aria-label="Pinned" className="size-3" /> : null}
            {t.is_private ? <LockIcon aria-label="Private" className="size-3" /> : null}
            <span
              className={`max-w-[24ch] truncate ${t.status === "resolved" ? "text-muted-foreground" : ""}`}
            >
              {t.name}
            </span>
            {unread > 0 ? (
              <CountPill
                data-slot="topic-unread"
                count={unread}
                tone="accent"
                aria-label={`${unread} unread`}
              />
            ) : null}
            {at ? (
              <span className="text-[11px] font-normal text-faint max-sm:hidden">
                {ago(at, now)}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

/** Marks the channel read up to the newest message, once, when the topic opens unread. */
function useMarkRead(
  chat: ChatContext,
  channelId: string,
  topicId: string,
  last: MessageView | undefined,
  unread: number,
) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const done = useRef<string | null>(null);
  const lastId = last?.id;
  useEffect(() => {
    if (!lastId || unread <= 0 || done.current === lastId) return;
    if (chat.readOnly || chat.meId === null) return;
    // An optimistic message is not the server's yet: mark read up to it once it is.
    if (lastId.startsWith("local-")) return;
    done.current = lastId;
    // CHAT-PERF: the pills drop now; the refetch after the call settles the true counts.
    markReadOptimistic(queryClient, co, channelId, topicId);
    void api.client.channels
      .read({ params: { co, id: channelId }, body: { message_id: lastId } })
      .catch(() => {})
      .finally(() =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) }),
          queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, channelId) }),
        ]),
      );
  }, [api, co, channelId, topicId, lastId, unread, chat.readOnly, chat.meId, queryClient]);
}

/** A `?thread=` link opens the side panel on its Thread tab. */
function useThreadPanel(thread: string | null) {
  const { setPanelOpen, setPanelTab } = useShellState();
  useEffect(() => {
    if (!thread) return;
    setPanelTab("thread");
    setPanelOpen(true);
  }, [thread, setPanelOpen, setPanelTab]);
}

/**
 * One place's conversation: an open topic, or (`main`) the channel's own conversation — its
 * messages, the agents' status line and the composer, which posts there and says so.
 * CHANNEL-TOPICS-MERGED (standing rule, 2026-10-30): the channel's own view always reads its
 * topics' latest messages too, each tagged with its topic (a chip that opens it), merged with
 * the channel's own messages in time order. The composer still posts to the channel itself.
 */
function TopicPane({
  chat,
  channel,
  topic,
  runs,
  now,
  main = false,
  topics = [],
}: {
  chat: ChatContext;
  channel: Channel;
  topic: TopicSummary;
  runs: readonly Run[];
  now: Date;
  /** The channel's own conversation (CHAT-TOPICS-SEPARATE), not a topic. */
  main?: boolean;
  /** `main` only: the channel's topics (not its own conversation), most active first. */
  topics?: readonly TopicSummary[];
}) {
  const { api, co, company } = chat;
  const navigate = useNavigate();
  const { focus } = useFocus();
  const running = runs.some((r) => r.state === "running" || r.state === "queued");
  // CHAT-DE: an asked agent's reply is awaited too (its ask's state changes without a run here).
  const [asked, setAsked] = useState(false);
  const messages = useInfiniteQuery({
    ...messagesQuery(api, co, topic.thread_id),
    refetchInterval: running || asked ? 5_000 : 60_000,
  });
  const list = useMemo(() => flatMessages(messages.data), [messages.data]);
  const waiting = workingAgents(list, now).length > 0;
  useEffect(() => setAsked(waiting), [waiting]);
  // CHANNEL-TOPICS-MERGED: the topics the viewer reads (its members'; the agency operator, with
  // no membership, reads all), their latest page each, merged in time order with the channel's.
  const readable = main
    ? topics
        .filter((t) => (t.is_member || chat.meId === null) && t.last_message_at)
        .slice(0, INTERLEAVED_TOPICS)
    : [];
  const topicPages = useQueries({
    queries: readable.map((t) => latestMessagesQuery(api, co, t.thread_id)),
  });
  const merged = readable.length
    ? interleave([
        { topicId: topic.id, messages: list },
        ...readable.map((t, i) => ({ topicId: t.id, messages: topicPages[i]?.data?.data ?? [] })),
      ])
    : list;
  const topicByThread = new Map(readable.map((t) => [t.thread_id, t]));
  const { split } = useThreadSplit(chat, merged);
  const ownRoots = readable.length
    ? split.roots.filter((m) => m.thread_id === topic.thread_id)
    : split.roots;
  const ids = useMemo(() => new Set(list.map((m) => m.id)), [list]);
  const inFlight = runsInTopic(runs, topic.id, ids);
  // CHAT-THREAD-AGENTS: an agent answering in a thread works there (the thread panel's line);
  // the main line only says "Riley is replying…" on the root, and keeps its own asks' lines.
  // CHAT-TOPICS-SEPARATE: a DM reads in DmPane; this pane is a topic or the channel's own line.
  const isDm = false;
  const mainRuns = mainLineRuns(inFlight, list, isDm);
  const replying = replyingByRoot(
    list,
    isDm,
    now,
    chat.agents ? inFlight : [],
    (id) => chat.members.get(id)?.name ?? "An agent",
  );
  const seats = useSeatLabels(chat);
  const { badges } = useAgentRoom(chat, channel);
  const threadReads = useThreadReads(chat);
  const suggestions = useSuggestions(chat, [topic.thread_id]);
  const deliveries = useDeliveries(chat, [topic.thread_id]);
  const send = usePost(chat, channel);
  // The "New" line stays where it was when the topic opened, after it is marked read.
  const unreadAtOpen = useRef<number | null>(null);
  if (unreadAtOpen.current === null && messages.isSuccess)
    unreadAtOpen.current = topic.unread_count;
  useMarkRead(chat, channel.id, topic.id, list.at(-1), topic.unread_count);
  useThreadPanel(focus.thread);
  // Actions on a merged topic message act in its own topic (the list carries its thread).
  const actions = useMessageActions(chat, topic.thread_id, merged, (b) =>
    send(topic.thread_id, { markdown: b.markdown, cardRefs: b.cardRefs }),
  );
  // THREAD-WAKE: the topic's top level is one conversation; its chip says who a follow-up wakes.
  const addressee = useAddressee(chat, topic.thread_id, null, conversationVersion(ownRoots));
  const view = toChatMessages(split.roots, {
    chat,
    replies: split.replies,
    runs,
    inFlight,
    seats,
    now,
    badges,
    threadReads,
    suggestions,
    deliveries,
    replying,
    // CHANNEL-TOPICS-MERGED: every topic message carries its topic; the channel's own carry none.
    topicChip: readable.length
      ? (m: MessageView) => {
          const t = topicByThread.get(m.thread_id);
          if (!t) return null;
          return (
            <Button
              variant="ghost"
              size="xs"
              data-slot="topic-chip"
              className="h-5 px-1.5 text-xs font-medium text-secondary hover:text-foreground"
              onClick={() =>
                void navigate({
                  to: "/c/$slug/chat/$channelId/$topicId",
                  params: { slug: company.slug, channelId: channel.id, topicId: t.id },
                })
              }
            >
              {t.status === "resolved" ? (
                <CircleCheckIcon data-icon="inline-start" className="text-success" />
              ) : (
                <HashIcon data-icon="inline-start" />
              )}
              {t.name}
            </Button>
          );
        }
      : undefined,
  });
  const emptyNote = main
    ? "No messages in this channel yet."
    : topic.archived_at
      ? "This topic is archived. Its messages stay; an admin can restore it."
      : "No messages yet. Say what this topic is about.";
  const label = channelLabel(channel, chat);
  return (
    <section
      aria-label={main ? `Channel: ${label}` : `Topic: ${topic.name}`}
      className="flex min-h-0 flex-1 flex-col"
    >
      {messages.isError ? (
        <div className="p-6">
          <LoadError what="The messages" error={messages.error} />
        </div>
      ) : messages.isPending ? (
        <div
          role="status"
          aria-label="Loading messages"
          className="p-6 text-sm text-muted-foreground"
        >
          Loading messages…
        </div>
      ) : (
        <Conversation
          chat={chat}
          messages={view}
          listProps={actions.listProps}
          commands={actions.commands}
          dialog={actions.dialog}
          lastReadId={lastReadId(ownRoots, { unread_count: unreadAtOpen.current ?? 0 })}
          hasEarlier={messages.hasNextPage}
          loadingEarlier={messages.isFetchingNextPage}
          onLoadEarlier={() => void messages.fetchNextPage()}
          emptyNote={emptyNote}
          statusLine={
            <>
              {chat.agents ? <AgentStatusLine chat={chat} runs={mainRuns} typing={[]} /> : null}
              {/* CHAT-DE: an asked agent without a run line here says it is working. */}
              <AgentWorkingLine
                messages={mainLineAsks(list, isDm)}
                skip={chat.agents ? mainRuns.map((r) => r.membership_id) : []}
              />
            </>
          }
          // CHAT-TOPICS-SEPARATE: no topic field; the placeholder says where this posts.
          topic={null}
          channel={channel}
          threadId={topic.thread_id}
          placeholder={
            main ? `Message #${channel.name}` : `Message #${channel.name} › ${topic.name}`
          }
          addressee={addressee}
          onSend={async (_name, value, files) => {
            await send(topic.thread_id, { value, files, addressee: addressee.outgoing() });
            addressee.sent();
          }}
        />
      )}
    </section>
  );
}

/** Sends to the topic a composer's field names: an existing one, or a new one. */
function useSendToTopic(chat: ChatContext, channel: Channel, topics: readonly TopicSummary[]) {
  const { api, co } = chat;
  const send = usePost(chat, channel);
  const queryClient = useQueryClient();
  return async (name: string, value: ComposerValue, files: ChatFile[] = []): Promise<string> => {
    const known = topics.find((t) => t.name.toLowerCase() === name.toLowerCase());
    // CHAT-PERF: a known topic shows the message at once; a new one is created first.
    const t =
      known ??
      (await api.client.channels.createTopic({ params: { co, id: channel.id }, body: { name } }));
    await send(t.thread_id, { value, files }, { fresh: !known });
    if (!known) void queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, channel.id) });
    return t.id;
  };
}

/**
 * CHAT-TOPICS-SEPARATE: a channel whose own conversation has no message yet (the server makes
 * its `(no topic)` topic lazily): its first message makes it, then posts there.
 * CHAT-PLACEMENT-FIX (2026-10-30): until then the feed is not blank when the channel's topics
 * hold messages (every message of a channel from before CHAT-TOPICS-SEPARATE is in a topic):
 * it reads the latest messages of the topics the viewer is in, each run of a topic under a chip
 * that opens it. Replies stay in their threads; the composer still posts to the channel itself.
 */
function EmptyChannelPane({
  chat,
  channel,
  anyThread,
  topics,
  runs,
  now,
}: {
  chat: ChatContext;
  channel: Channel;
  /**
   * AGENT-MENTION: who a mention wakes is the channel's (its access and wake rule), so any of its
   * topics' threads answers the @ menu's readiness until the channel's own one exists.
   */
  anyThread: string | null;
  /** The channel's topics (not its own conversation), most active first. */
  topics: readonly TopicSummary[];
  runs: readonly Run[];
  now: Date;
}) {
  const { api, co, company } = chat;
  const send = usePost(chat, channel);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { focus } = useFocus();
  useThreadPanel(focus.thread);
  // Topics the viewer reads: its members' (the agency operator, with no membership, reads all).
  const readable = topics
    .filter((t) => (t.is_member || chat.meId === null) && t.last_message_at)
    .slice(0, INTERLEAVED_TOPICS);
  const pages = useQueries({
    queries: readable.map((t) => latestMessagesQuery(api, co, t.thread_id)),
  });
  const all = interleave(
    readable.map((t, i) => ({ topicId: t.id, messages: pages[i]?.data?.data ?? [] })),
  );
  const { split } = useThreadSplit(chat, all);
  const seats = useSeatLabels(chat);
  const threadReads = useThreadReads(chat);
  const topicOf = new Map(all.map((m) => [m.id, m.topicId]));
  const byId = new Map(readable.map((t) => [t.id, t]));
  let previous: string | undefined;
  const view = toChatMessages(split.roots, {
    chat,
    replies: split.replies,
    runs,
    inFlight: [],
    seats,
    now,
    threadReads,
    replying: replyingByRoot(all, false, now),
    topicChip: (m: MessageView) => {
      // A located root carries no topic: it shows under the chip of the run it starts.
      const id = topicOf.get(m.id) ?? previous;
      if (!id || id === previous) return null;
      previous = id;
      const t = byId.get(id);
      return (
        <Button
          variant="ghost"
          size="xs"
          data-slot="topic-chip"
          className="h-5 px-1.5 text-xs font-medium text-secondary hover:text-foreground"
          onClick={() =>
            void navigate({
              to: "/c/$slug/chat/$channelId/$topicId",
              params: { slug: company.slug, channelId: channel.id, topicId: id },
            })
          }
        >
          {t?.status === "resolved" ? (
            <CircleCheckIcon data-icon="inline-start" className="text-success" />
          ) : (
            <HashIcon data-icon="inline-start" />
          )}
          {t?.name ?? "Topic"}
        </Button>
      );
    },
  });
  if (readable.length > 0 && pages.some((p) => p.isPending))
    return (
      <div
        role="status"
        aria-label="Loading messages"
        className="p-6 text-sm text-muted-foreground"
      >
        Loading messages…
      </div>
    );
  return (
    <section
      aria-label={`Channel: ${channelLabel(channel, chat)}`}
      className="flex min-h-0 flex-1 flex-col"
    >
      <Conversation
        chat={chat}
        messages={view}
        emptyNote="No messages in this channel yet."
        channel={channel}
        topic={null}
        threadId={anyThread}
        draftPlace={`channel:${channel.id}`}
        placeholder={`Message #${channel.name}`}
        onSend={async (_name, value, files) => {
          const t = await api.client.channels.createTopic({
            params: { co, id: channel.id },
            body: { name: MAIN_TOPIC },
          });
          // CHAT-PERF: the conversation's list starts with the message; no wait for a fetch.
          await send(t.thread_id, { value, files }, { fresh: true });
          void queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, channel.id) });
        }}
      />
    </section>
  );
}

const INTERLEAVED_TOPICS = STREAM_TOPICS;

/**
 * A DM: one conversation. Its topics' messages read as one stream (the latest page of its most
 * active topics, one call each); a message goes to its most active topic, else its implicit one.
 */
function DmPane({
  chat,
  channel,
  topics,
  runs,
  now,
}: {
  chat: ChatContext;
  channel: Channel;
  topics: readonly TopicSummary[];
  runs: readonly Run[];
  now: Date;
}) {
  const { api, co } = chat;
  const { focus } = useFocus();
  const recent = topics.slice(0, INTERLEAVED_TOPICS);
  const pages = useQueries({
    queries: recent.map((t) => latestMessagesQuery(api, co, t.thread_id)),
  });
  const seats = useSeatLabels(chat);
  const { badges } = useAgentRoom(chat, channel);
  const threadReads = useThreadReads(chat);
  const suggestions = useSuggestions(
    chat,
    recent.map((t) => t.thread_id),
  );
  const deliveries = useDeliveries(
    chat,
    recent.map((t) => t.thread_id),
  );
  const sendTo = useSendToTopic(chat, channel, topics);
  const send = usePost(chat, channel);
  useThreadPanel(focus.thread);
  const all = interleave(
    recent.map((t, i) => ({ topicId: t.id, messages: pages[i]?.data?.data ?? [] })),
  );
  // CHAT-CONTROLS: replies stay off the main line and count under their root ("n replies").
  // CHAT-PLACEMENT-FIX: a reply whose root is older than the loaded page waits under that root,
  // fetched for it, never on the main line.
  const { split } = useThreadSplit(chat, all);
  const latest = recent[0];
  const actions = useMessageActions(chat, latest?.thread_id ?? "", all, (b) =>
    latest
      ? send(latest.thread_id, { markdown: b.markdown, cardRefs: b.cardRefs })
      : Promise.resolve(),
  );
  const view = toChatMessages(split.roots, {
    chat,
    replies: split.replies,
    runs,
    inFlight: [],
    seats,
    now,
    badges,
    threadReads,
    suggestions,
    deliveries,
    // CHAT-THREAD-AGENTS: an agent answering in a thread shows as "replying…" on its root.
    // (CHAT-TOPICS-SEPARATE: this pane is a DM's only, so no topic chips.)
    replying: replyingByRoot(all, true, now),
  });
  if (pages.some((p) => p.isPending))
    return (
      <div
        role="status"
        aria-label="Loading messages"
        className="p-6 text-sm text-muted-foreground"
      >
        Loading messages…
      </div>
    );
  return (
    <Conversation
      chat={chat}
      messages={view}
      listProps={actions.listProps}
      dialog={actions.dialog}
      emptyNote="No messages yet."
      statusLine={<AgentWorkingLine messages={mainLineAsks(all, true)} />}
      channel={channel}
      topic={null}
      // AGENT-MENTION: who a mention wakes is the channel's (its access and wake rule), so any
      // of its topics' threads answers the @ menu's readiness.
      threadId={topics[0]?.thread_id ?? null}
      // CHAT-SWITCH-A: the DM keeps its own draft, whatever its first topic is.
      draftPlace={`stream:${channel.id}`}
      placeholder={`Message ${channelLabel(channel, chat)}`}
      onSend={async (_name, value, files) => {
        // A DM posts into its one conversation (its most active topic, else its implicit one).
        // CHAT-PERF: no refetch: the message is patched into the stream's cached pages.
        await sendTo(topics[0]?.name ?? MAIN_TOPIC, value, files);
      }}
    />
  );
}
